import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { API_URL } from '../../config';

// Import GraphQL queries and mutations
import { 
  GET_HELP_TUTORIALS, 
  CREATE_HELP_TUTORIAL, 
  DELETE_HELP_TUTORIAL,
  UPDATE_HELP_TUTORIAL 
} from '../../graphql/helpTutorials';

// Define available roles (similar to AddNoteModal)
const AVAILABLE_ROLES = [
  'ADMIN',
  'HOUSEKEEPER',
  'HOUSEKEEPER_MANAGER', 
  'PROPERTY_MANAGER',
  'INSPECTOR',
  'MAINTENANCE',
  'CALL_CENTER'
];

const ROLE_LABELS = {
  ADMIN: 'Admin',
  HOUSEKEEPER: 'Housekeeper',
  HOUSEKEEPER_MANAGER: 'Housekeeper Manager',
  PROPERTY_MANAGER: 'Property Manager', 
  INSPECTOR: 'Inspector',
  MAINTENANCE: 'Maintenance',
  CALL_CENTER: 'Call Center'
};

const MAX_MEDIA_FILE_SIZE = 100 * 1024 * 1024; // 100 MiB

const HelpTutorialsSection = () => {
  // State for tutorial form
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [tutorialToDelete, setTutorialToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [editingTutorial, setEditingTutorial] = useState(null);
  const [isPublic, setIsPublic] = useState(true);
  const [selectedRoles, setSelectedRoles] = useState({});
  
  // GraphQL hooks
  const { data: tutorialsData, loading: loadingTutorials, refetch: refetchTutorials } = useQuery(GET_HELP_TUTORIALS);
  const [createHelpTutorial, { loading: createLoading }] = useMutation(CREATE_HELP_TUTORIAL);
  const [deleteHelpTutorial, { loading: deleteLoading }] = useMutation(DELETE_HELP_TUTORIAL);
  const [updateHelpTutorial, { loading: updateLoading }] = useMutation(UPDATE_HELP_TUTORIAL);
  
  // Initialize tutorials array
  const tutorials = tutorialsData?.helpTutorials || [];
  
  // Initialize roles state
  useEffect(() => {
    const initialRoles = AVAILABLE_ROLES.reduce((acc, role) => {
      acc[role] = false;
      return acc;
    }, {});
    setSelectedRoles(initialRoles);
  }, []);

  // Handle role toggle
  const handleRoleToggle = (role) => {
    setSelectedRoles(prev => ({
      ...prev,
      [role]: !prev[role]
    }));
  };

  // Media dropzone handler (videos and images)
  const handleMediaDrop = async (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setVideoUploading(true);
      
      try {
        // Validate file size before requesting an upload URL.
        if (file.size > MAX_MEDIA_FILE_SIZE) {
          toast.error("File is too large. Maximum size is 100MB.");
          setVideoUploading(false);
          return;
        }
        
        // Store the file for later upload
        setVideoFile(file);
        
        // Create a preview
        const objectUrl = URL.createObjectURL(file);
        setVideoPreview(objectUrl);
        
        const fileType = file.type.startsWith('image/') ? 'Image' : 'Video';
        toast.success(`${fileType} selected. Save the tutorial to upload it.`);
      } catch (error) {
        console.error("Error processing uploaded file:", error);
        toast.error("There was an error processing your file. Please try a different file.");
      } finally {
        setVideoUploading(false);
      }
    }
  };

  const handleMediaDropRejected = (fileRejections) => {
    const isTooLarge = fileRejections.some(({ errors }) =>
      errors.some(({ code }) => code === 'file-too-large')
    );

    toast.error(isTooLarge
      ? 'File is too large. Maximum size is 100MB.'
      : 'Please select one supported video or image file.');
  };
  
  // Media dropzone hook (videos and images)
  const { 
    getRootProps: getVideoRootProps, 
    getInputProps: getVideoInputProps, 
    isDragActive: isVideoDragActive 
  } = useDropzone({
    onDrop: handleMediaDrop,
    onDropRejected: handleMediaDropRejected,
    accept: {
      'video/*': ['.mp4', '.webm', '.mov', '.avi'],
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp']
    },
    maxSize: MAX_MEDIA_FILE_SIZE,
    maxFiles: 1,
    multiple: false
  });
  
  // Handle media upload to S3 (videos and images)
  const handleMediaUpload = async () => {
    if (!videoFile) return null;
    
    setVideoUploading(true);
    setVideoUploadProgress(0);
    
    try {
      // Get pre-signed URL for upload
      const response = await fetch(`${API_URL}/api/help-tutorials/get-upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // This sends HTTP-only cookies automatically
        body: JSON.stringify({
          fileName: videoFile.name,
          fileType: videoFile.type,
          fileSize: videoFile.size
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload URL request failed:', response.status, errorText);
        throw new Error(`Failed to get upload URL: ${response.status} ${response.statusText}`);
      }
      
      const { uploadUrl, videoUrl: uploadedMediaUrl, headers } = await response.json();
      
      // Upload file with progress tracking
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setVideoUploadProgress(progress);
        }
      };
      
      // Create a promise to handle the XHR upload
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.open('PUT', uploadUrl, true);
        xhr.timeout = 10 * 60 * 1000;
        
        // Set headers
        if (headers) {
          Object.keys(headers).forEach(key => {
            xhr.setRequestHeader(key, headers[key]);
          });
        }
        
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(uploadedMediaUrl);
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => {
          reject(new Error('Upload failed due to network error'));
        };

        xhr.ontimeout = () => {
          reject(new Error('Upload timed out. Please check your connection and try again.'));
        };

        xhr.onabort = () => {
          reject(new Error('Upload was canceled'));
        };
        
        console.log('Uploading file:', videoFile.name, 'Size:', videoFile.size, 'Type:', videoFile.type);
        xhr.send(videoFile);
      });
      
      // Wait for upload to complete
      const finalMediaUrl = await uploadPromise;
      
      // Return the URL of the uploaded media
      return finalMediaUrl;
    } catch (error) {
      console.error('Error uploading media:', error);
      const fileType = videoFile?.type?.startsWith('image/') ? 'image' : 'video';
      toast.error(`Failed to upload ${fileType}: ` + error.message);
      return null;
    } finally {
      setVideoUploading(false);
    }
  };
  
  // Handle edit button click
  const handleEditClick = (tutorial) => {
    console.log('Edit button clicked for tutorial:', tutorial);
    setEditingTutorial(tutorial);
    setTitle(tutorial.title);
    setContent(tutorial.content);
    setVideoPreview(tutorial.videoUrl);
    setIsPublic(tutorial.isPublic || false);
    
    // Set selected roles
    const roles = AVAILABLE_ROLES.reduce((acc, role) => {
      acc[role] = tutorial.visibleToRoles ? tutorial.visibleToRoles.includes(role) : false;
      return acc;
    }, {});
    setSelectedRoles(roles);
    
    // Scroll to form
    setTimeout(() => {
      const formElement = document.querySelector('form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingTutorial(null);
    setTitle('');
    setContent('');
    setVideoFile(null);
    setVideoPreview(null);
    setIsPublic(true);
    
    // Reset roles
    const initialRoles = AVAILABLE_ROLES.reduce((acc, role) => {
      acc[role] = false;
      return acc;
    }, {});
    setSelectedRoles(initialRoles);
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    if (!content.trim()) {
      toast.error('Content is required');
      return;
    }

    // Validate role selection if not public
    if (!isPublic) {
      const hasSelectedRoles = Object.values(selectedRoles).some(selected => selected);
      if (!hasSelectedRoles) {
        toast.error('Please select at least one role or make the tutorial public');
        return;
      }
    }
    
    try {
      let videoUrl = null;
      
      // Upload media if exists
      if (videoFile) {
        videoUrl = await handleMediaUpload();
        if (!videoUrl) {
          return;
        }
      }

      // Prepare visible roles
      const visibleToRoles = isPublic ? null : Object.keys(selectedRoles).filter(role => selectedRoles[role]);
      
      if (editingTutorial) {
        // Update existing tutorial
        const updateInput = {
          title,
          content,
          videoUrl: videoUrl || editingTutorial.videoUrl,
          isPublic,
          visibleToRoles
        };
        
        console.log('Updating tutorial with input:', updateInput);
        
        const { data } = await updateHelpTutorial({
          variables: {
            id: Number(editingTutorial.id),
            input: updateInput
          }
        });
        
        console.log('Update response:', data);
        
        if (data?.updateHelpTutorial) {
          toast.success('Tutorial updated successfully');
          handleCancelEdit();
          refetchTutorials();
        }
      } else {
        // Create new tutorial
        const { data } = await createHelpTutorial({
          variables: {
            input: {
              title,
              content,
              videoUrl,
              isPublic,
              visibleToRoles
            }
          }
        });
        
        if (data?.createHelpTutorial) {
          toast.success('Tutorial created successfully');
          
          // Reset form
          setTitle('');
          setContent('');
          setVideoFile(null);
          setVideoPreview(null);
          setIsPublic(true);
          
          // Reset roles
          const initialRoles = AVAILABLE_ROLES.reduce((acc, role) => {
            acc[role] = false;
            return acc;
          }, {});
          setSelectedRoles(initialRoles);
          
          // Refresh tutorials list
          refetchTutorials();
        }
      }
    } catch (error) {
      console.error('Error saving tutorial:', error);
      toast.error(error.message || 'Failed to save tutorial');
    }
  };
  
  // Delete tutorial handler
  const handleDeleteTutorial = async () => {
    if (!tutorialToDelete) return;
    
    try {
      const { data } = await deleteHelpTutorial({
        variables: { id: Number(tutorialToDelete.id) }
      });
      
      if (data?.deleteHelpTutorial === true) {
        toast.success('Tutorial deleted successfully');
        refetchTutorials();
      } else {
        toast.error('Failed to delete tutorial');
      }
    } catch (error) {
      console.error('Error deleting tutorial:', error);
      toast.error(error.message || 'Failed to delete tutorial');
    } finally {
      setShowDeleteConfirm(false);
      setTutorialToDelete(null);
    }
  };
  
  // Configure ReactQuill modules
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet',
    'link'
  ];
  
  return (
    <div className="space-y-8">
      <div className="pb-5 mb-5 border-b border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900">Help Tutorials</h3>
        <p className="mt-2 max-w-4xl text-sm text-gray-500">
          Create and manage help tutorials that can be displayed to users throughout the application.
        </p>
      </div>
      
      {/* Add/Edit Tutorial Form */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {editingTutorial ? 'Edit Tutorial' : 'Add New Tutorial'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {editingTutorial ? 'Update the existing tutorial' : 'Create tutorials to help users navigate the application'}
          </p>
        </div>
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Tutorial Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="px-4 py-2.5 h-11 w-full border border-gray-200 rounded-md bg-white text-gray-900 placeholder-gray-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 text-sm transition-colors"
                placeholder="Enter tutorial title"
                required
              />
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Tutorial Content</label>
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write tutorial content here..."
                  className="h-40"
                />
              </div>
            </div>

            {/* Visibility Settings */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">Visibility Settings</label>
              
              {/* Public/Private Toggle */}
              <div className="flex items-center">
                <input
                  id="isPublic"
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                />
                <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-700">
                  Make this tutorial public (accessible without login)
                </label>
              </div>

              {/* Role Selection (only show if not public) */}
              {!isPublic && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Visible To Roles
                  </label>
                  <div className="p-3 bg-gray-50 rounded-md mb-3">
                    <p className="text-sm text-gray-600">
                      Select which user roles can view this tutorial. Users must be logged in and have one of the selected roles.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {AVAILABLE_ROLES.map(role => (
                      <label key={role} className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={!!selectedRoles[role]}
                          onChange={() => handleRoleToggle(role)}
                          className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {ROLE_LABELS[role] || role}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Media Upload (Optional)</label>
              <div
                {...getVideoRootProps()}
                className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors border-gray-300 hover:border-blue-500"
              >
                <input {...getVideoInputProps()} />
                {isVideoDragActive ? (
                  <p className="text-blue-500">Drop the file here...</p>
                ) : (
                  <div>
                    <p className="text-sm text-gray-600">
                      <span className="text-blue-600">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">Videos: MP4, WebM, MOV, AVI | Images: JPG, PNG, GIF, WebP (up to 100MB)</p>
                  </div>
                )}
              </div>
              
              {videoPreview && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {videoFile?.type?.startsWith('image/') ? 'Image Preview:' : 'Video Preview:'}
                  </p>
                  <div className="relative border border-gray-200 rounded-md overflow-hidden">
                    {videoFile?.type?.startsWith('image/') ? (
                      <img
                        src={videoPreview}
                        alt="Preview"
                        className="w-full h-auto max-h-60 object-contain"
                      />
                    ) : (
                      <video
                        src={videoPreview}
                        controls
                        className="w-full h-auto max-h-60"
                      />
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setVideoFile(null);
                        setVideoPreview(null);
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 focus:outline-none"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              
              {videoUploading && (
                <div className="mt-4">
                  <div className="text-sm text-gray-700 mb-1">Uploading: {videoUploadProgress}%</div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${videoUploadProgress}%` }}></div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={videoUploading || createLoading || updateLoading}
                className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
              >
                {videoUploading
                  ? 'Uploading...'
                  : createLoading || updateLoading
                    ? 'Saving...'
                    : (editingTutorial ? 'Update Tutorial' : 'Create Tutorial')}
              </button>
              {editingTutorial && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
      
      {/* Existing Tutorials List */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
        <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Existing Tutorials</h3>
          <p className="mt-1 text-sm text-gray-500">
            View and manage existing help tutorials
          </p>
        </div>
        <div className="p-6">
          {loadingTutorials ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
            </div>
          ) : tutorials.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No tutorials available. Create your first one above.
            </div>
          ) : (
            <div className="space-y-6">
              {tutorials.map(tutorial => (
                <div key={tutorial.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                    <h4 className="font-medium text-gray-900">{tutorial.title}</h4>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">ID: {tutorial.id}</span>
                      <button
                        onClick={() => handleEditClick(tutorial)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-gray-100"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          setTutorialToDelete(tutorial);
                          setShowDeleteConfirm(true);
                        }}
                        className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-gray-100"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: tutorial.content }}></div>
                    
                    {tutorial.videoUrl && (
                      <div className="mt-4 border border-gray-200 rounded-md overflow-hidden">
                        {/* Check if it's an image by file extension */}
                        {/\.(jpg|jpeg|png|gif|webp)$/i.test(tutorial.videoUrl) ? (
                          <img
                            src={tutorial.videoUrl}
                            alt="Tutorial media"
                            className="w-full h-auto max-h-60 object-contain"
                          />
                        ) : (
                          <video
                            src={tutorial.videoUrl}
                            controls
                            className="w-full h-auto max-h-60"
                          />
                        )}
                      </div>
                    )}
                    
                    <div className="mt-3 text-sm text-gray-500">
                      Created on: {new Date(tutorial.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && tutorialToDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Deletion</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the tutorial "<span className="font-semibold">{tutorialToDelete.title}</span>"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTutorial}
                className="px-4 py-2 border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none disabled:opacity-50"
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpTutorialsSection;
